import { ApiError } from "@/api/errors";

import type { ScanProblem } from "@/pages/stock/types";

export const BACKTEST_PERIODS = ["6M", "1Y", "2Y", "3Y", "5Y"] as const;
export type BacktestPeriod = (typeof BACKTEST_PERIODS)[number];

export const DEFAULT_UNIVERSE = "nifty500";
export const DEFAULT_EXPR = "close > sma(close, 200) and rsi(14) crosses above 50";

/** "rsi_oversold_bounce" → "RSI oversold bounce" */
export function scanTitle(name: string): string {
  const words = name.replace(/[_-]+/g, " ").trim();
  const fixed = words.replace(/\b(rsi|macd|adx|nr7|rs|52w)\b/gi, (w) => w.toUpperCase());
  return fixed.charAt(0).toUpperCase() + fixed.slice(1);
}

/** A friendly universe label: "nifty500" → "NIFTY 500", "A,B,C" → "3 symbols". */
export function universeLabel(universe: string): string {
  const u = universe.trim();
  const named: Record<string, string> = { nifty50: "NIFTY 50", nifty500: "NIFTY 500", all: "All NSE stocks" };
  const name = named[u.toLowerCase()];
  if (name) return name;
  if (u.includes(",")) return `${u.split(",").filter(Boolean).length} symbols`;
  return u.toUpperCase();
}

/** The scan problem inside a failed run or back-test (FastAPI's 400 detail), if any. */
export function scanProblem(error: unknown): ScanProblem | null {
  if (!(error instanceof ApiError) || error.status !== 400) return null;
  const d = error.detail as { error?: unknown; column?: unknown; hint?: unknown } | null;
  if (d && typeof d === "object" && typeof d.error === "string") {
    return {
      message: d.error,
      column: typeof d.column === "number" ? d.column : null,
      hint: typeof d.hint === "string" ? d.hint : null,
    };
  }
  return null;
}

/** Keep the scan on one line, character for character, so error columns line up. */
export function oneLine(text: string): string {
  return text.replace(/[\r\n\t]/g, " ");
}

/** Is a result column numeric (worth right-aligning and formatting)? */
export function isNumericColumn(rows: Record<string, unknown>[], col: string): boolean {
  return rows.some((r) => typeof r[col] === "number");
}
